import type { Aabb, Vec3 } from "./types";

/**
 * Slab method ray-vs-AABB. Returns the entry t (in dir's units) of intersection,
 * or null if the ray misses or only hits behind the origin within [0, maxT].
 */
export function rayVsAabb(origin: Vec3, dir: Vec3, box: Aabb, maxT: number): number | null {
  let tmin = 0, tmax = maxT;
  for (const axis of ["x", "y", "z"] as const) {
    const o = origin[axis], d = dir[axis];
    const lo = box.min[axis], hi = box.max[axis];
    if (Math.abs(d) < 1e-8) {
      if (o < lo || o > hi) return null;
    } else {
      const inv = 1 / d;
      let t1 = (lo - o) * inv;
      let t2 = (hi - o) * inv;
      if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
      if (t1 > tmin) tmin = t1;
      if (t2 < tmax) tmax = t2;
      if (tmin > tmax) return null;
    }
  }
  return tmin >= 0 ? tmin : null;
}

/** Aim direction from yaw/pitch (matches client camera order YXZ). */
export function aimDir(yaw: number, pitch: number): Vec3 {
  const cp = Math.cos(pitch);
  return {
    x: -Math.sin(yaw) * cp,
    y:  Math.sin(pitch),
    z: -Math.cos(yaw) * cp,
  };
}

/** AABB enclosing a player at position (feet) with given radius/height. */
export function playerAabb(pos: Vec3, radius: number, height: number): Aabb {
  return {
    min: { x: pos.x - radius, y: pos.y,          z: pos.z - radius },
    max: { x: pos.x + radius, y: pos.y + height, z: pos.z + radius },
  };
}
